import { LegalPage, LegalSection } from './LegalPage';
import { ExternalLinkIcon } from '../components/icons';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { ANDROID_TESTING, APP_LINKS } from '../data/appLinks';
import './AndroidPage.css';

const STEPS = [
  {
    title: 'Join the Tulink Testers group',
    body: 'Open the group, sign in with your Google account and tap “Join group” next to the group name. Only group members can install the Android test build.',
    action: 'Join the group',
    href: ANDROID_TESTING.group,
  },
  {
    title: 'Become a tester',
    body: 'Open the testing page and tap “Become a tester”. This unlocks Tulink for your account on Google Play.',
    action: 'Become a tester',
    href: ANDROID_TESTING.optIn,
  },
  {
    title: 'Install Tulink',
    body: 'Open Tulink on Google Play and tap Install. If it says the app is not available, wait a few minutes and try again.',
    action: 'Open Google Play',
    href: ANDROID_TESTING.store,
  },
];

/**
 * Walkthrough for the Play closed test. The order matters: Play only offers the
 * opt-in to group members, and only offers the install to testers who opted in.
 */
export const AndroidPage = () => {
  useDocumentTitle('Get Tulink on Android — Tulink');

  return (
    <LegalPage
      eyebrow="Android early access"
      title="Get Tulink on Android"
      lede="Tulink for Android is in early testing on Google Play. Three quick steps and you are in."
    >
      <LegalSection id="before-you-start" title="Before you start">
        <p className="tl-android__account">
          Use the same Google account for every step — the one signed in to the Play Store on your
          phone. Joining with a different account is the most common reason the app does not show
          up.
        </p>
      </LegalSection>

      <LegalSection id="steps" title="Three steps">
        <ol className="tl-android__steps" aria-label="Android setup steps">
          {STEPS.map((step, index) => (
            <li key={step.href} className="tl-android__step">
              <span className="tl-android__step-number" aria-hidden="true">
                {index + 1}
              </span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                <a
                  className="tl-button tl-button--primary tl-android__action"
                  href={step.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {step.action}
                  <ExternalLinkIcon />
                </a>
              </div>
            </li>
          ))}
        </ol>
      </LegalSection>

      <LegalSection id="iphone" title="On an iPhone?">
        <p>
          Tulink for iOS installs through Apple’s TestFlight.{' '}
          <a href={APP_LINKS.ios} target="_blank" rel="noopener noreferrer">
            Get Tulink on TestFlight
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
};
